// S2-0f.1-S1: Data hook — fetch agent list + per-agent detail (health status)
import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';
import type { AgentSummary, AgentDetail } from '../types/eval';

/** Agent list item enriched with health from the detail endpoint */
export interface AgentListItem extends AgentSummary {
  health: 'ready' | 'not-ready' | 'conditional' | 'unknown';
  quality_overall: number | null;
  loading_detail: boolean;
}

/** Derive health status from the latest run's quality score and classification */
function deriveHealth(detail: AgentDetail): AgentListItem['health'] {
  // If a readiness verdict exists, use it directly
  if (detail.latest_readiness) {
    const v = detail.latest_readiness.verdict;
    if (v === 'READY') return 'ready';
    if (v === 'CONDITIONAL') return 'conditional';
    return 'not-ready';
  }
  // Fallback: derive from latest run quality
  if (detail.latest_run) {
    if (detail.latest_run.quality_overall >= 0.85) return 'ready';
    if (detail.latest_run.quality_overall >= 0.7) return 'conditional';
    return 'not-ready';
  }
  return 'unknown';
}

export function useAgents() {
  const [agents, setAgents] = useState<AgentListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Get the agent list
      const list = await apiFetch<AgentSummary[]>('/v1/eval/agents');

      // Initialize with unknown health while detail loads
      const initial: AgentListItem[] = list.map(a => ({
        ...a,
        health: 'unknown' as const,
        quality_overall: null,
        loading_detail: true,
      }));
      setAgents(initial);

      // Step 2: Enrich each agent with detail (health, quality)
      // Done in parallel — fine for <10 agents
      const enriched = await Promise.all(
        list.map(async (agent) => {
          try {
            const detail = await apiFetch<AgentDetail>(`/v1/eval/agents/${agent.id}`);
            return {
              ...agent,
              health: deriveHealth(detail),
              quality_overall: detail.latest_run?.quality_overall ?? null,
              loading_detail: false,
            } as AgentListItem;
          } catch {
            // If detail fetch fails, keep the agent with unknown health
            return {
              ...agent,
              health: 'unknown' as const,
              quality_overall: null,
              loading_detail: false,
            } as AgentListItem;
          }
        })
      );

      setAgents(enriched);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load agents';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  return { agents, loading, error, refetch: fetchAgents };
}
